import { useMemo, useState,useEffect, useRef } from "react";
import { supabase } from "../../supabaseClient";
import { FaUserCircle } from "react-icons/fa";
import { IoSend } from "react-icons/io5";
import { FiMenu } from "react-icons/fi";
import { IoColorPalette } from "react-icons/io5";
import { IoPencil } from "react-icons/io5";
import { emojiMap } from './assets/EmoticonMap.jsx';
import renderMessageContent from "./Emoji-img";
import { usePublicUser } from "./data/usePublicUserID";
import { useFriends } from "./data/fetchFriends";
import { useChatRooms } from "./data/useChatRooms";
import { useMessages } from "./data/useMessages";
import LeaveChatModal from './LeaveChatModal.jsx';
import AddPeopleModal from './data/AddPeopleModal.jsx'; 
import SettingsModal from './data/NameColorPicker.jsx';

import "./MessengerUI.css";



const fmtRelTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);

  const toTime = (dt) =>
    dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  if (isSameDay(d, now)) return `Today ${toTime(d)}`;
  if (isSameDay(d, yesterday)) return `Yesterday ${toTime(d)}`;

  // e.g., Jan 12 4:32 PM
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const truncate = (s, n = 32) => (s.length > n ? s.slice(0, n - 1) + "…" : s);

// -----------------

export const MessagesUI = () => {
  // current user (from your users table, includes public_user_id)
  const { publicUser, loading: userLoading, error: userError } = usePublicUser();
  const currentUserId = publicUser?.public_user_id || null;
const [showLeaveModal, setShowLeaveModal] = useState(false);
  const { friends = [], loading: friendsLoading } = useFriends(currentUserId);

  // rooms the user is in (participants resolved to {id, username}), sorted by last activity
  const { chatRooms, loading: chatLoading, refresh } = useChatRooms(currentUserId);
const [showAddModal, setShowAddModal] = useState(false);
  const [isRenamingChat, setIsRenamingChat] = useState(false);
  const [chatNameDraft, setChatNameDraft] = useState("");

  const [selectedFriends, setSelectedFriends] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 599 : false
  );
  const [mobilePane, setMobilePane] = useState("inbox");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [sidebarQuery, setSidebarQuery] = useState("");
  const [sidebarSection, setSidebarSection] = useState("chats");
  const [unreadByChat, setUnreadByChat] = useState({});
const [userColor, setUserColor] = useState("#003366");
const [isShaking, setIsShaking] = useState(false);
const [someoneTyping, setSomeoneTyping] = useState(null);
  const latestSnapshotRef = useRef({});
  const unreadInitializedRef = useRef(false);
  const chatBodyRef = useRef(null);
  const messagesEndRef = useRef(null);
  const unreadStorageKey = currentUserId ? `fb-unread-chats-${currentUserId}` : "";

  const scrollToLatestMessage = () => {
    const chatBodyEl = chatBodyRef.current;
    if (!chatBodyEl) return;

    chatBodyEl.scrollTop = chatBodyEl.scrollHeight;
    messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
  };


const sendNudge = async () => {
  if (!activeChatId) return;

  await sendMessage(currentUserId, "/nudge"); // store as a special system command
};

useEffect(() => {
  const channel = supabase
    .channel("typing")
    .on("broadcast", { event: "typing" }, (payload) => {
      if (
        payload.payload.roomId === activeChatId &&
        payload.payload.userId !== currentUserId
      ) {
        setSomeoneTyping(payload.payload.username);

        setTimeout(() => setSomeoneTyping(null), 2000); // auto-clear
      }
    })
    .subscribe();

  return () => supabase.removeChannel(channel);
}, [activeChatId, currentUserId]);



useEffect(() => {
    
  if (!currentUserId) return;

  // Initialize color
  if (publicUser?.name_color) setUserColor(publicUser.name_color);

  // Create channel specifically for the users table
  const channel = supabase
    .channel("realtime-user-color", {
      config: { broadcast: { self: true } }, // allow receiving own updates
    })
.on(
  "postgres_changes",
  {
    event: "UPDATE",
    schema: "public",
    table: "users",
  },
  (payload) => {
    console.log("⚡ Realtime event received:", payload);
    const updated = payload.new;
    if (!updated) return;

    if (updated.public_user_id === currentUserId) {
      setUserColor(updated.name_color);
    }
    refresh();
  }
)
    .subscribe((status) => {
      console.log("Realtime status:", status);
    });

  return () => {
    supabase.removeChannel(channel);
  };
}, [currentUserId, publicUser, refresh]);


  // messages for the active chat
  const activeRoom = useMemo(
  () => chatRooms.find((r) => r.id === activeChatId) || null,
  [chatRooms, activeChatId]
);

  const { messages, loading: messagesLoading, sendMessage } = useMessages(activeChatId);
const activeRoomLabel = activeRoom
  ? (activeRoom.type === "private"
      ? activeRoom.participants.filter(p => p.id !== currentUserId).map(p => p.username).join(", ")
      : activeRoom.name || activeRoom.participants.filter(p => p.id !== currentUserId).map(p => p.username).join(", "))
  : "";

  const availableToAdd = friends.filter(
  f => !activeRoom?.participants.some(p => p.id === f.public_user_id)
);
const handleAddPeople = async (newUserIds) => {
  if (!activeChatId || newUserIds.length === 0) return;
  const rows = newUserIds.map(uid => ({ chat_id: activeChatId, user_id: uid }));

  const { error } = await supabase.from("chat_participants").insert(rows);
  if (error) {
    console.error("Error adding people:", error);
    window.alert(`Could not add people to this chat: ${error.message}`);
  } else {
    const addedNames = newUserIds
      .map((userId) => friends.find((friend) => friend.public_user_id === userId)?.username)
      .filter(Boolean)
      .join(", ");

    const { error: messageError } = await supabase.from("chat_messages").insert([{
      chat_id: activeChatId,
      sender_id: currentUserId,
      content: `[system]${publicUser.username} has added ${addedNames} to the chatroom`,
    }]);

    if (messageError) {
      console.error("Error creating chat update message:", messageError);
    }

    await refresh(); // reload rooms
    setShowAddModal(false); // close modal
  }
};

const renameChat = async () => {
  const name = chatNameDraft.trim();
  if (!activeChatId || !name) return;

  const { error } = await supabase
    .from("chat_rooms")
    .update({ name })
    .eq("id", activeChatId);

  if (error) {
    console.error("Error renaming chat:", error);
    window.alert(`Could not rename this chat: ${error.message}`);
    return;
  }

  setIsRenamingChat(false);
  await refresh();
};

const closeSidebarOnMobile = () => {
  if (isMobile) {
    setMobilePane("chat");
    setShowSidebar(false);
  }
};

const openChat = (chatId) => {
  setActiveChatId(chatId);
  if (isMobile) setMobilePane("chat");
  setUnreadByChat((prev) => {
    if (!prev[chatId]) return prev;
    return { ...prev, [chatId]: 0 };
  });
  closeSidebarOnMobile();

  // Ensure we jump to the latest message after the room mounts.
  window.requestAnimationFrame(() => {
    scrollToLatestMessage();
  });
};

useEffect(() => {
  const onResize = () => {
    const mobile = window.innerWidth <= 599;
    setIsMobile(mobile);
    if (!mobile) {
      setMobilePane("chat");
      setShowSidebar(true);
    }
  };

  onResize();
  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}, []);

useEffect(() => {
  const last = messages[messages.length - 1];
  if (!last) return;

  const isNudge = last.content === "/nudge";
  const fromOther = last.sender_id !== currentUserId;

  if (isNudge && fromOther) {
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 800);
  }
}, [messages, currentUserId]);

useEffect(() => {
  if (!activeChatId) return;

  // Run twice to catch late layout shifts (emoji/image render, font paint).
  const rafId = window.requestAnimationFrame(() => {
    scrollToLatestMessage();
  });
  const timeoutId = window.setTimeout(() => {
    scrollToLatestMessage();
  }, 120);

  return () => {
    window.cancelAnimationFrame(rafId);
    window.clearTimeout(timeoutId);
  };
}, [messages, activeChatId, messagesLoading]);
const handleTyping = async (text) => {
  setNewMessage(text);
if (!activeChatId) return;
  await supabase.channel("typing")
    .send({
      type: "broadcast",
      event: "typing",
      payload: {
        userId: currentUserId,
        roomId: activeChatId,
        username: publicUser?.username
      }
    });
};

// LEAVE CHAT: core logic
const leaveChat = async () => {
  try {
    if (!activeChatId || !currentUserId) return;

    // 1) remove me from participants
    const { data: remaining, error: remErr } = await supabase
      .from("chat_participants")
      .select("user_id", { count: "exact", head: false })
      .eq("chat_id", activeChatId);
    if (remErr) throw remErr;

    if (remaining?.length > 1) {
      const whoLeft = publicUser?.username || "Someone";
      const { error: messageError } = await supabase.from("chat_messages").insert([{
        chat_id: activeChatId,
        sender_id: currentUserId,
        content: `[system]${whoLeft} has left the chat.`,
      }]);
      if (messageError) throw messageError;
    }

    const { error: delErr } = await supabase
      .from("chat_participants")
      .delete()
      .eq("chat_id", activeChatId)
      .eq("user_id", currentUserId);
    if (delErr) throw delErr;

    if (!remaining || remaining.length === 0) {

      await supabase.from("chat_messages").delete().eq("chat_id", activeChatId);
      await supabase.from("chat_rooms").delete().eq("id", activeChatId);
    }

    setShowLeaveModal(false);
    setActiveChatId(null);
    if (isMobile) setMobilePane("inbox");
    await refresh(); // reload chat list
  } catch (e) {
    console.error("Leave chat error:", e);
  }
};


const displayRoomName = useMemo(() => {
  if (!activeRoom) return "";

  const otherUsers = activeRoom.participants
    .filter(p => p.id !== currentUserId)
    .map(p => p.username);

  // If only ONE other user → show ONLY their name
  if (otherUsers.length === 1) return otherUsers[0];

  // If multiple users → then use real group name
  return activeRoom.name || otherUsers.join(", ");
}, [activeRoom, currentUserId]);


  const toggleFriendSelection = (friendUsername) => {
    setSelectedFriends((prev) =>
      prev.includes(friendUsername)
        ? prev.filter((n) => n !== friendUsername)
        : [...prev, friendUsername]
    );
  };

const startChat = async (usernames) => {
  if (!currentUserId || !usernames || usernames.length === 0) return;

  // Convert usernames to IDs
  const selectedIds = usernames
    .map(username => friends.find(f => f.username === username)?.public_user_id)
    .filter(Boolean);

  // Always create GROUP chats, but store room name as ALL participants
  // Example stored name: "Jamie, Test"
  const roomName = [publicUser.username, ...usernames].join(", ");

  const { data: newChatId, error } = await supabase.rpc("create_chat_room", {
    room_name: roomName,
    room_type: "group",
    member_ids: selectedIds,
  });

  if (error) {
    console.error("Error creating chat:", error);
    return;
  }

  await refresh();
  openChat(newChatId);
};

useEffect(() => {
  if (!currentUserId || !activeChatId) return;

  const storageKey = `fb-last-read-chat-map-${currentUserId}`;
  const latestSeen = messages[messages.length - 1]?.created_at || new Date().toISOString();

  try {
    const existing = JSON.parse(localStorage.getItem(storageKey) || "{}") || {};
    existing[activeChatId] = latestSeen;
    localStorage.setItem(storageKey, JSON.stringify(existing));
  } catch (error) {
    // Keep chat functional even if localStorage is unavailable.
  }
}, [currentUserId, activeChatId, messages]);

useEffect(() => {
  if (!unreadStorageKey) return;
  try {
    const raw = localStorage.getItem(unreadStorageKey);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      setUnreadByChat(parsed);
    }
  } catch (error) {
    console.error("Failed to load unread chat counts:", error);
  }
}, [unreadStorageKey]);

useEffect(() => {
  if (!unreadStorageKey) return;
  localStorage.setItem(unreadStorageKey, JSON.stringify(unreadByChat));
}, [unreadByChat, unreadStorageKey]);

useEffect(() => {
  if (!chatRooms.length) return;

  if (!unreadInitializedRef.current) {
    const snapshot = {};
    const initialUnread = {};
    let lastReadByChat = {};

    try {
      lastReadByChat = JSON.parse(
        localStorage.getItem(`fb-last-read-chat-map-${currentUserId}`) || "{}"
      ) || {};
    } catch (error) {
      console.error("Failed to load last-read chat times:", error);
    }

    chatRooms.forEach((room) => {
      const latest = room.latestMessage;
      const latestAt = latest?.created_at || "";
      const lastReadAt = lastReadByChat[room.id] || "";

      snapshot[room.id] = latestAt;
      if (
        latestAt &&
        latestAt > lastReadAt &&
        latest?.sender_id !== currentUserId &&
        !latest?.content?.startsWith("[system]")
      ) {
        initialUnread[room.id] = 1;
      }
    });

    latestSnapshotRef.current = snapshot;
    unreadInitializedRef.current = true;
    setUnreadByChat((prev) => ({ ...initialUnread, ...prev }));
    return;
  }

  setUnreadByChat((prev) => {
    let changed = false;
    const next = { ...prev };
    const nextSnapshot = { ...latestSnapshotRef.current };

    chatRooms.forEach((room) => {
      const latest = room.latestMessage;
      const latestAt = latest?.created_at;
      if (!latestAt) return;

      const previousLatestAt = latestSnapshotRef.current[room.id];
      const isNewLatest = latestAt !== previousLatestAt;

      if (room.id === activeChatId) {
        if ((next[room.id] || 0) !== 0) {
          next[room.id] = 0;
          changed = true;
        }
      } else if (
        isNewLatest &&
        latest.sender_id !== currentUserId
      ) {
        next[room.id] = (next[room.id] || 0) + 1;
        changed = true;
      }

      nextSnapshot[room.id] = latestAt;
    });

    latestSnapshotRef.current = nextSnapshot;
    return changed ? next : prev;
  });
}, [chatRooms, activeChatId, currentUserId]);

const filteredFriends = useMemo(() => {
  const q = sidebarQuery.trim().toLowerCase();
  if (!q) return friends;
  return friends.filter((friend) =>
    (friend.username || "").toLowerCase().includes(q)
  );
}, [friends, sidebarQuery]);

const filteredChatRooms = useMemo(() => {
  if (sidebarSection === "unread") {
    return chatRooms.filter((room) => (unreadByChat[room.id] || 0) > 0);
  }

  const q = sidebarQuery.trim().toLowerCase();
  if (!q) return chatRooms;

  return chatRooms.filter((room) => {
    const roomName =
      room.type === "private"
        ? room.participants
            .filter((p) => p.id !== currentUserId)
            .map((p) => p.username)
            .join(", ")
        : room.name || "";

    const preview = roomPreview(room);
    return (
      roomName.toLowerCase().includes(q) ||
      preview.toLowerCase().includes(q)
    );
  });
}, [chatRooms, currentUserId, sidebarQuery, sidebarSection, unreadByChat]);

const sortedChatRooms = useMemo(() => {
  const rooms = [...filteredChatRooms];
  rooms.sort((a, b) => {
    if (a.id === activeChatId) return -1;
    if (b.id === activeChatId) return 1;

    const unreadA = unreadByChat[a.id] || 0;
    const unreadB = unreadByChat[b.id] || 0;
    if (unreadA !== unreadB) return unreadB - unreadA;

    return 0;
  });
  return rooms;
}, [filteredChatRooms, activeChatId, unreadByChat]);

const getRoomName = (room) => {
  if (!room) return "";
  if (room.type === "private") {
    return room.participants
      .filter((p) => p.id !== currentUserId)
      .map((p) => p.username)
      .join(", ");
  }
  return room.name;
};

const totalUnread = useMemo(
  () => Object.values(unreadByChat).reduce((sum, value) => sum + (value || 0), 0),
  [unreadByChat]
);

const unreadRooms = useMemo(
  () => sortedChatRooms.filter((room) => (unreadByChat[room.id] || 0) > 0),
  [sortedChatRooms, unreadByChat]
);






  const handleSend = async () => {
    if (!newMessage.trim() || !activeChatId || !currentUserId) return;
    await sendMessage(currentUserId, newMessage.trim());
    setNewMessage("");
  };

  // sidebar preview helpers

  function roomPreview(room) {
    if (room.latestMessage) {
      const fromMe = room.latestMessage.sender_id === currentUserId;
      const prefix = fromMe
        ? "You: "
        : (() => {
            const who =
              room.participants.find((p) => p.id === room.latestMessage.sender_id)?.username ||
              "";
            return `${who}: `;
          })();
      return truncate(prefix + room.latestMessage.content, 40);
    }
    // No messages yet → show "Created on Mon DD" (your choice D)
    const created = new Date(room.created_at);
    const stamp = created.toLocaleString([], { month: "short", day: "numeric" });
    return `Created on ${stamp}`;
  }

  if (userLoading || chatLoading) return <p>Loading…</p>;
  if (userError) return <p>Error: {userError.message}</p>;
  if (!currentUserId) return <p>Not signed in.</p>;

return (
 <div className={`msn-container ${isShaking ? "shake-screen" : ""} ${isMobile ? `mobile-pane-${mobilePane}` : ""}`}>

    {/* HEADER — Classic Blue MSN Bar */}
    <div className="msn-header">
      MSN Reborn
    </div>

    <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
      {/* SIDEBAR */}
{/* SIDEBAR */}
{(!isMobile || mobilePane === "inbox") && (
<div className={`msn-sidebar ${showSidebar ? "show" : ""}`}>
  <div className="msn-sidebar__topbar">
    {isMobile && (
      <button
        type="button"
        className="msn-sidebar__close-mobile"
        onClick={() => {
          setMobilePane("chat");
          setShowSidebar(false);
        }}
      >
        Close
      </button>
    )}
    <input
      type="text"
      value={sidebarQuery}
      onChange={(event) => setSidebarQuery(event.target.value)}
      className="msn-sidebar__search"
      placeholder="Search contacts or chats"
    />
    <div className="msn-sidebar__filters">
      <button
        type="button"
        className={`msn-filter-btn ${sidebarSection === "chats" ? "active" : ""}`}
        onClick={() => setSidebarSection("chats")}
      >
        Chats
      </button>
      <button
        type="button"
        className={`msn-filter-btn ${sidebarSection === "unread" ? "active" : ""}`}
        onClick={() => setSidebarSection("unread")}
      >
        Unread
        {totalUnread > 0 && (
          <span className="msn-filter-badge" aria-label={`${totalUnread} unread chats`}>
            {totalUnread}
          </span>
        )}
      </button>
      <button
        type="button"
        className={`msn-filter-btn ${sidebarSection === "contacts" ? "active" : ""}`}
        onClick={() => setSidebarSection("contacts")}
      >
        Contacts
      </button>
      <button
        type="button"
        className={`msn-filter-btn ${sidebarSection === "all" ? "active" : ""}`}
        onClick={() => setSidebarSection("all")}
      >
        All
      </button>
    </div>
  </div>

  {/* CONTACT LIST */}
  {(sidebarSection === "all" || sidebarSection === "contacts") && (
    <>
      <div className="msn-section-title">Contacts ({filteredFriends.length})</div>

{filteredFriends.map((f) => (
  <div
    key={f.public_user_id}
    className="friend-item"
    onClick={() => startChat([f.username])}
  >
    <FaUserCircle className="friend-avatar" />
    <span>{f.username}</span>
  </div>
))}

      {filteredFriends.length === 0 && (
        <p className="msn-empty">No contacts found.</p>
      )}
    </>
  )}


  {/* SEPARATOR LINE */}
  {(sidebarSection === "all" || sidebarSection === "contacts") &&
    (sidebarSection === "all" || sidebarSection === "chats") && (
      <div className="msn-separator"></div>
    )}

  {/* ACTIVE CHAT ROOMS */}
  {(sidebarSection === "all" || sidebarSection === "chats" || sidebarSection === "unread") && (
    <>
      <div className="msn-section-title">
        {sidebarSection === "unread"
          ? `Unread Chats (${filteredChatRooms.length})`
          : `Your Chats (${filteredChatRooms.length})`}
      </div>

  {sortedChatRooms.map((room) => (
    <div
      key={room.id}
      className={`chatroom-item ${
        activeChatId === room.id ? "active-chatroom" : ""
      }`}
      onClick={() => openChat(room.id)}
    >
      {/* Room name */}
      <div className="chatroom-name">
        <span>
          {getRoomName(room)}
        </span>
        {(unreadByChat[room.id] || 0) > 0 && (
          <span className="chatroom-badge">{unreadByChat[room.id]}</span>
        )}
      </div>

      {/* Preview (latest message or created date) */}
      <div className="chatroom-preview">
        {roomPreview(room)}
      </div>
    </div>
  ))}

      {filteredChatRooms.length === 0 && (
        <p className="msn-empty">No chats found.</p>
      )}
    </>
  )}
</div>
)}


      {/* MAIN CHAT SECTION */}
  <div className={`chat-section ${isMobile && mobilePane !== "chat" ? "is-hidden-mobile" : ""}`}>
        <div className="mobile-chat-switcher">
          <button
            type="button"
            className="mobile-chat-switcher__menu"
            onClick={() => setShowSidebar((prev) => !prev)}
          >
            Chats
            {totalUnread > 0 && (
              <span className="mobile-chat-switcher__pill">{totalUnread}</span>
            )}
          </button>

          <div className="mobile-chat-switcher__list">
            {sortedChatRooms.slice(0, 8).map((room) => (
              <button
                key={`mobile-${room.id}`}
                type="button"
                className={`mobile-chat-chip ${activeChatId === room.id ? "active" : ""}`}
                onClick={() => openChat(room.id)}
              >
                <span className="mobile-chat-chip__label">{getRoomName(room)}</span>
                {(unreadByChat[room.id] || 0) > 0 && (
                  <span className="mobile-chat-chip__badge">{unreadByChat[room.id]}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* TAB BAR */}
        <div className="chat-tabs">
          {activeRoom && (
            <div className="chat-tab active">
              {displayRoomName}
              <span
                className="close-tab"
                onClick={() => {
                  setActiveChatId(null);
                  if (isMobile) setMobilePane("inbox");
                }}
              >
                ×
              </span>
            </div>
          )}
        </div>

        {/* CHAT HEADER BAR */}
        <div className="chat-header">
          <button
            className="menu-btn"
            onClick={() => {
              if (isMobile) {
                setMobilePane("inbox");
              } else {
                setShowSidebar(!showSidebar);
              }
            }}
          >
            {isMobile ? "Inbox" : <FiMenu />}
            {totalUnread > 0 && <span className="menu-btn__badge">{totalUnread}</span>}
          </button>
<button className="msn-btn" onClick={sendNudge}>
  🫨 Nudge
</button>


          <div className="chat-header__identity">
            {isRenamingChat ? (
              <form className="chat-rename-form" onSubmit={(event) => {
                event.preventDefault();
                renameChat();
              }}>
                <input
                  autoFocus
                  value={chatNameDraft}
                  maxLength={80}
                  onChange={(event) => setChatNameDraft(event.target.value)}
                  aria-label="Chat name"
                />
                <button type="submit" className="msn-btn">Save</button>
                <button type="button" className="msn-btn" onClick={() => setIsRenamingChat(false)}>Cancel</button>
              </form>
            ) : (
              <div className="chat-header__title-row">
                <h4>{displayRoomName}</h4>
                {activeRoom && (
                  <button
                    type="button"
                    className="chat-rename-button"
                    title="Rename chat"
                    aria-label="Rename chat"
                    onClick={() => {
                      setChatNameDraft(activeRoom.name || displayRoomName);
                      setIsRenamingChat(true);
                    }}
                  >
                    <IoPencil />
                  </button>
                )}
              </div>
            )}
            {activeRoom && (
              <div className="chat-participants" title="People in this chat">
                {activeRoom.participants.map((participant) => participant.username).join(", ")}
              </div>
            )}
          </div>

          {/* Add people button */}
          {activeRoom && activeRoom.type === "group" && (
            <button
              className="msn-btn"
              onClick={() => setShowAddModal(true)}
            >
              Add People
            </button>
          )}

          {/* Leave chat */}
          {activeRoom && (
            <button
              className="msn-btn"
              onClick={() => setShowLeaveModal(true)}
            >
              Leave
            </button>
          )}
        </div>

        {/* MESSAGES */}
        <div className="chat-body" ref={chatBodyRef}>
          {!activeChatId && (
            <div className="msn-chat-placeholder">
              Select a chat from the sidebar to start messaging.
            </div>
          )}

          {!activeChatId && unreadRooms.length > 0 && (
            <div className="msn-recommendations">
              <div className="msn-recommendations__title">Unread Conversations</div>
              {unreadRooms.slice(0, 4).map((room) => (
                <button
                  key={`unread-${room.id}`}
                  type="button"
                  className="msn-recommendations__item"
                  onClick={() => openChat(room.id)}
                >
                  <span>{getRoomName(room)}</span>
                  <span className="chatroom-badge">{unreadByChat[room.id]}</span>
                </button>
              ))}
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`chat-message-wrapper ${
                msg.isSystem
                  ? "system-message"
                  : msg.sender_id === currentUserId
                  ? "sent"
                  : "received"
              }`}
            >
              <div className="chat-bubble">
                {!msg.isSystem && (
                  <div
                    className="sender-name"
                    style={{ color: msg.name_color }}
                  >
                    {msg.senderName}
                  </div>
                )}

                {/* message content (supports emojis) */}
                <div>{renderMessageContent(msg.content.replace(/^\[system\]/, ""))}</div>

                <div className="timestamp">
                  {fmtRelTime(msg.created_at)}
                </div>
                {someoneTyping && (
  <div className="typing-indicator">
    {someoneTyping} is typing…
  </div>
)}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* INPUT BAR */}
        <div className="chat-input">
          {/* Emoji button */}
          <button
            className="emoji-btn"
            onClick={() => setShowEmojiPicker((x) => !x)}
          >
            🙂
          </button>

          {/* typing input */}
          <input
            type="text"
            value={newMessage}
            onChange={(e) => handleTyping(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleSend();
              }
            }}
            className="msn-input"
            placeholder="Type a message…"
          />

          <button
            className="msn-btn color-button"
            title="Change your chat name color"
            onClick={() => setShowSettingsModal(true)}
          >
            <IoColorPalette />
          </button>

          {/* send */}
          <button
            className="msn-send-btn"
            onClick={handleSend}
          >
            <IoSend />
          </button>

          {/* Emoji picker */}
{showEmojiPicker && (
  <div className="emoji-picker">
    {Object.entries(emojiMap).map(([key, emoji]) => (
      <button
        key={key}
        className="emoji-btn-item"
        onClick={() => {
          setNewMessage((prev) => prev + " " + key + " ");
          setShowEmojiPicker(false);
        }}
      >
        <img src={emoji} alt={key} className="emoji-inline" />
      </button>
    ))}
  </div>
)}
        </div>
      </div>
    </div>

    {/* POPUP MODALS */}
    <SettingsModal
      open={showSettingsModal}
      onClose={() => setShowSettingsModal(false)}
      userId={currentUserId}
      onColorChange={setUserColor}
    />

<AddPeopleModal
  open={showAddModal}
  friends={availableToAdd}                
  existing={activeRoom?.participants || []}
  onCancel={() => setShowAddModal(false)} 
  onConfirm={handleAddPeople}          
  />

<LeaveChatModal
  open={showLeaveModal}
  chatName={displayRoomName}
  onCancel={() => setShowLeaveModal(false)}
  onConfirm={leaveChat}
/>

  </div>
);

}
